import { useEffect, useState } from "react";
import { fetchAccess } from "../services/expensesService";

// ---------------------------------------------------------------------------
// ¿La persona conectada puede ver Gastos y Presupuestos? (#608)
//
// Se pregunta al servidor UNA vez por sesión y usuario (`GET /expensesAccess`,
// que responde 200 con `allowed: false` en vez de 403: un 403 cerraría la
// sesión). El resultado se guarda en sessionStorage para no repetir la llamada
// en cada pantalla. Mientras no hay respuesta la app no se muestra.
// ---------------------------------------------------------------------------

export type AccessState = "unknown" | "allowed" | "denied" | "error";

const KEY = "expensesAccess";
/** Vigencia del resultado guardado: un cambio en la lista se nota a los pocos minutos. */
const TTL_MS = 5 * 60 * 1000;
let inflight: Promise<AccessState> | null = null;
let inflightUser: string | null = null;
const listeners = new Set<(s: AccessState) => void>();

const currentUser = () => (typeof localStorage !== "undefined" ? localStorage.getItem("userId") : null);

function readCache(userId: string | null): AccessState {
  if (!userId) return "unknown";
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return "unknown";
    const parsed = JSON.parse(raw) as { userId: string; state: AccessState; at?: number };
    if (parsed.userId !== userId || !(parsed.state === "allowed" || parsed.state === "denied")) return "unknown";
    if (!parsed.at || Date.now() - parsed.at > TTL_MS) return "unknown";
    return parsed.state;
  } catch {
    return "unknown";
  }
}

function writeCache(userId: string, state: AccessState, locked: boolean) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ userId, state, locked, at: Date.now() }));
  } catch {
    /* sin sessionStorage: se volverá a preguntar */
  }
}

// ¿La lista de acceso está fija en código (`accessLocked`, CC-CONSOLE #1007)? Lo
// dice la misma respuesta de `GET /expensesAccess`; el menú lo usa para no pintar
// «Acceso» donde no hay nada que editar. Es solo presentación, así que lo
// guardado sirve aunque haya vencido: evita que el ítem parpadee al entrar.
const lockedListeners = new Set<(locked: boolean) => void>();

function readLocked(userId: string | null): boolean {
  if (!userId) return false;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(KEY) || "null") as { userId?: string; locked?: boolean } | null;
    return parsed?.userId === userId && parsed.locked === true;
  } catch {
    return false;
  }
}

export function resolveExpensesAccess({ fresh = false } = {}): Promise<AccessState> {
  const userId = currentUser();
  if (!userId || !localStorage.getItem("token")) return Promise.resolve("unknown");
  const cached = fresh ? "unknown" : readCache(userId);
  if (cached !== "unknown") return Promise.resolve(cached);
  if (inflight && inflightUser === userId) return inflight;
  inflightUser = userId;
  inflight = fetchAccess()
    .then((a) => ({ state: (a.allowed ? "allowed" : "denied") as AccessState, locked: a.accessLocked === true }))
    .catch(() => ({ state: "error" as AccessState, locked: null }))
    .then(({ state, locked }) => {
      if (locked !== null) {
        writeCache(userId, state, locked);
        lockedListeners.forEach((l) => l(locked));
      }
      inflight = null;
      listeners.forEach((l) => l(state));
      return state;
    });
  return inflight;
}

/**
 * `true` cuando el servidor dijo que la lista de acceso no se puede ampliar. No
 * pregunta por su cuenta: escucha la respuesta que pide `useExpensesAccess`.
 * Sin respuesta (o con error) es `false`: no se esconde nada a ciegas.
 */
export function useExpensesAccessLocked(): boolean {
  const [locked, setLocked] = useState(() => readLocked(currentUser()));
  useEffect(() => {
    lockedListeners.add(setLocked);
    return () => {
      lockedListeners.delete(setLocked);
    };
  }, []);
  return locked;
}

/** Olvida el resultado (al cambiar la lista de acceso o cerrar sesión). */
export function clearExpensesAccess() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nada */
  }
}

/**
 * `fresh`: la pantalla de la app no confía en lo guardado — espera la respuesta
 * del servidor antes de mostrar secciones (si se le quitó el acceso, sus
 * llamadas responderían 403 y el interceptor cerraría la sesión).
 */
export function useExpensesAccess(enabled = true, { fresh = false } = {}): AccessState {
  const [state, setState] = useState<AccessState>(() => (fresh ? "unknown" : readCache(currentUser())));
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const listener = (s: AccessState) => alive && setState(s);
    listeners.add(listener);
    void resolveExpensesAccess({ fresh }).then((s) => alive && setState(s));
    return () => {
      alive = false;
      listeners.delete(listener);
    };
  }, [enabled, fresh]);
  return state;
}
